import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {Listing} from '../models/listing';


@Injectable({
  providedIn: 'root' // This makes it available app-wide
})

export class Globals {
  // Reactive store to notify changes to any property
  private _globalsSubject = new BehaviorSubject<GlobalsState>({
    isInitialLoad: true,
    hostnameStr: '',
    siteIDStr: 3069,
    linksEnabledBln: true,
    emailStr: '',
    userIDStr: '',
    clientId: '',
    savedSearchIDStr: '',
    ipAddrStr: '',
    sidStr: '',
    isRegisteredBln: '0',
    initBln: true,
    loginBln: true,
    debugBln: false,
    loadingBln: false,
    timeoutNum: 0,
    delayObj: {},
    initLoadCompleteBln: true,
    page: 1,
    lastPage: 0,
    limitNum: 10,
    startNum: 0,
    endNum: 10,
    maxPreviewNum: 2,
    viewedNum: 0,
    scrollerObj: {},
    statusStr: 'Active,Sold',
    stateStr: 'ON',
    idStr: 3069,
    countNum: 0,
    propertyTypeStr: 'Not Specified',
    districtList: '',
    searchMin: '0',
    searchMax: '300000000',
    bedroomsNum: 'Any',
    bathroomsNum: 'Any',
    squareFeetStr: 'Any',
    listingsViewedNum: 0,
    listings: [],
    selectedListing: undefined,
    routeScrollPositions: {},
    scrollTopNum: 0,
    mobileBln: false,
    previousURLStr: '',
    previousStatusStr: '',
    enableExclusiveListingsBln: true,
    routeStr: '',
    previousRouteStr: '',
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    utmTerm: '',
    utmContent: '',
  });

  // Expose subject as an observable for reactive subscriptions
  public globals$ = this._globalsSubject.asObservable();

  // Explicit property declarations from GlobalsState
  isInitialLoad: boolean | undefined;
  hostnameStr!: string;
  siteIDStr!: number;
  linksEnabledBln!: boolean;
  emailStr!: string;
  userIDStr!: string;
  clientId!: string;
  savedSearchIDStr!: string;
  ipAddrStr!: string;
  sidStr!: string;
  isRegisteredBln!: string;
  initBln!: boolean;
  loginBln!: boolean;
  debugBln!: boolean;
  loadingBln!: boolean;
  timeoutNum!: number;
  delayObj!: object;
  initLoadCompleteBln!: boolean;
  page!: number;
  lastPage!: number;
  limitNum!: number;
  startNum!: number;
  endNum!: number;
  maxPreviewNum!: number;
  viewedNum!: number;
  scrollerObj!: object;
  statusStr!: string;
  stateStr!: string;
  idStr!: number;
  countNum!: number;
  propertyTypeStr!: string;
  districtList!: string;
  searchMin!: string;
  searchMax!: string;
  bedroomsNum!: string;
  bathroomsNum!: string;
  squareFeetStr!: string;
  listingsViewedNum!: number;
  listings!: Listing[];
  selectedListing!: Listing | undefined;
  routeScrollPositions!: Record<string, number>;
  scrollTopNum!: number;
  mobileBln!: boolean;
  previousURLStr!: string;
  previousStatusStr!: string;
  enableExclusiveListingsBln!: boolean;
  routeStr!: string;
  previousRouteStr!: string;
  utmSource!: string;
  utmMedium!: string;
  utmCampaign!: string;
  utmTerm!: string;
  utmContent!: string;

  constructor() {
    this.setupDynamicGettersAndSetters();
  }

  // Dynamically define getters and setters for all state properties
  private setupDynamicGettersAndSetters(): void {
    const state = this._globalsSubject.value;

    Object.keys(state).forEach((key) => {
      Object.defineProperty(this, key, {
        // Getter
        get: () => this._globalsSubject.value[key as keyof GlobalsState],
        // Setter
        set: (value: any) => {
          const currentState = this._globalsSubject.value[key as keyof GlobalsState];
          if (currentState !== value) { // Only update if the value is different
            // Add specific logging for districtList
            if (key === 'districtList') {
              console.log('Attempting to set districtList:', value);
              console.log('Current state:', this._globalsSubject.value);
            }

            //console.log(`Updating ${key} to ${value}`); // Debugging
            const updatedState = {
              ...this._globalsSubject.value,
              [key]: value,
            };
            this._globalsSubject.next(updatedState); // Notify subscribers
            // Verify the update
            if (key === 'districtList') {
              console.log('New state after update:', this._globalsSubject.value);
            }

          } else {
            //console.log(`No update for ${key}, value already set as ${value}`);
          }
        },
        enumerable: true,
        configurable: true,
      });
    });
  }
}

// Define the interface for the state
export interface GlobalsState {
  isInitialLoad: boolean;
  hostnameStr: string;
  siteIDStr: number;
  linksEnabledBln: boolean;
  emailStr: string;
  userIDStr: string;
  clientId: string;
  savedSearchIDStr: string;
  ipAddrStr: string;
  sidStr: string;
  isRegisteredBln: string;
  initBln: boolean;
  loginBln: boolean;
  debugBln: boolean;
  loadingBln: boolean;
  timeoutNum: number;
  delayObj: object;
  initLoadCompleteBln: boolean;
  page: number;
  lastPage: number;
  limitNum: number;
  startNum: number;
  endNum: number;
  maxPreviewNum: number;
  viewedNum: number;
  scrollerObj: object;
  statusStr: string;
  stateStr: string;
  idStr: number;
  countNum: number;
  propertyTypeStr: string;
  districtList: string;
  searchMin: string;
  searchMax: string;
  bedroomsNum: string;
  bathroomsNum: string;
  squareFeetStr: string;
  listingsViewedNum: number;
  listings: Listing[];
  selectedListing: Listing | undefined;
  routeScrollPositions: Record<string, number>;
  scrollTopNum: number;
  mobileBln: boolean;
  previousURLStr: string;
  previousStatusStr: string;
  enableExclusiveListingsBln: boolean;
  routeStr: string;
  previousRouteStr: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
}
